import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import Markdown from 'react-markdown'

import { ReactComponent as AIBadgeSparkle } from 'src/assets/svg/ai-badge-sparkle.svg'
import { ReactComponent as CopyIcon } from 'src/assets/svg/copy.svg'
import { ReactComponent as MicrophoneIcon } from 'src/assets/svg/microphone.svg'
import { ReactComponent as SendArrowIcon } from 'src/assets/svg/send-arrow.svg'
import { TypewriterText } from 'src/components/TypewriterText'
import { useAIAssistant } from 'src/contexts/AIAssistantContext'
import { CreditsData } from 'src/interfaces/credits'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { useCreateAiChatConversationMutation } from 'src/reactQuery/useCreateAiChatConversationMutation'
import { useGetAiChatConversationQuery } from 'src/reactQuery/useGetAiChatConversationQuery'
import { useGetCreditsQuery } from 'src/reactQuery/useGetCreditsQuery'
import { useSendAiChatMessageMutation } from 'src/reactQuery/useSendAiChatMessageMutation'

import styles from './aiAssistant.module.scss'
import { CreditLimitModal } from './CreditLimitModal'
import { SupportContactModal } from './SupportContactModal'

// Lock icon for credit limit reached state
const CreditLockIcon = () => (
  <svg
    fill="none"
    height="48"
    viewBox="0 0 48 48"
    width="48"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect fill="black" fillOpacity="0.3" height="48" rx="12" width="48" />
    <path
      d="M24.412 32L22.464 25.972C22.3965 25.782 22.271 25.6181 22.1051 25.5034C21.9393 25.3888 21.7415 25.3293 21.54 25.3333C21.3384 25.3293 21.1407 25.3888 20.9748 25.5034C20.809 25.6181 20.6834 25.782 20.616 25.972L18.668 32M28.0013 25.3333V32M19.3853 30H23.6933"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
    <path
      d="M13.6913 33.1266C13.9913 35.3533 15.8353 37.0986 18.0807 37.2013C19.9687 37.2879 21.8873 37.3333 24.0007 37.3333C26.114 37.3333 28.0327 37.288 29.9207 37.2C32.166 37.0986 34.01 35.3533 34.31 33.1266C34.506 31.6733 34.6673 30.184 34.6673 28.6666C34.6673 27.1493 34.506 25.66 34.31 24.2066C34.01 21.98 32.166 20.2346 29.9207 20.132C27.9486 20.0418 25.9747 19.9978 24.0007 20C21.8873 20 19.9687 20.0453 18.0807 20.1333C15.8353 20.2346 13.9913 21.98 13.6913 24.2066C13.494 25.66 13.334 27.1493 13.334 28.6666C13.334 30.184 13.4953 31.6733 13.6913 33.1266Z"
      stroke="white"
      strokeWidth="1.5"
    />
    <path
      d="M18 20V16.6666C18 15.0753 18.6321 13.5492 19.7574 12.424C20.8826 11.2988 22.4087 10.6666 24 10.6666C25.5913 10.6666 27.1174 11.2988 28.2426 12.424C29.3679 13.5492 30 15.0753 30 16.6666V20"
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

interface DisplayMessage {
  id: string
  isUser: boolean
  text: string
}

export const AIAssistantPage: FC = () => {
  const [question, setQuestion] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [pendingMessage, setPendingMessage] = useState<string | null>(null)
  const [localMessages, setLocalMessages] = useState<DisplayMessage[]>([])
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null
  )
  const [credits, setCredits] = useState<CreditsData | null>(null)

  const queryClient = useQueryClient()

  // Fetch credits from API
  const { data: creditsResponse } = useGetCreditsQuery()

  // Sync credits from query response
  useEffect(() => {
    if (creditsResponse?.data) {
      setCredits(creditsResponse.data)
    }
  }, [creditsResponse])

  // A question is billed twice (the question and the answer), and the backend
  // rejects a send whose balance would not cover the whole turn. Locking only
  // at zero left the last few credits looking spendable: the input stayed
  // enabled, the send went out, and the only feedback was a 403. Lock on the
  // same threshold the backend enforces.
  const costPerInteraction =
    credits?.costPerInteraction ??
    (credits?.costPerMessage ? credits.costPerMessage * 2 : 10)
  const isCreditsExhausted =
    credits !== null && credits.available < costPerInteraction

  // Calculate time until reset
  const [resetTimeDisplay, setResetTimeDisplay] = useState('')

  useEffect(() => {
    const calculateTimeUntilReset = () => {
      if (!credits?.resetsAt) {
        setResetTimeDisplay('')
        return
      }

      const now = new Date()
      const resetTime = new Date(credits.resetsAt)

      const diffMs = resetTime.getTime() - now.getTime()
      if (diffMs <= 0) {
        setResetTimeDisplay('soon')
        return
      }

      const hours = Math.floor(diffMs / (1000 * 60 * 60))
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

      setResetTimeDisplay(`${hours}h ${minutes}m`)
    }

    calculateTimeUntilReset()

    // Update every minute
    const interval = setInterval(calculateTimeUntilReset, 60000)

    return () => clearInterval(interval)
  }, [credits?.resetsAt])

  const [isCreditLimitModalOpen, setIsCreditLimitModalOpen] = useState(false)
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false)

  // Show credit limit modal when credits reach zero
  useEffect(() => {
    if (isCreditsExhausted) {
      setIsCreditLimitModalOpen(true)
    }
  }, [isCreditsExhausted])

  // Use ref to avoid stale closure in mutation callbacks
  const pendingMessageRef = useRef<string | null>(null)

  const {
    clearConversation,
    conversationId,
    setConversationId,
    setHasConversation,
  } = useAIAssistant()

  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Fetch conversation with messages
  const { data: conversationData, isLoading: isLoadingConversation } =
    useGetAiChatConversationQuery({
      conversationId,
    })

  // Send message mutation
  const sendMessageMutation = useSendAiChatMessageMutation({
    onError: () => {
      setIsTyping(false)
      setPendingMessage(null)
      pendingMessageRef.current = null
    },
    onInsufficientCredits: (error) => {
      setIsTyping(false)
      setPendingMessage(null)
      pendingMessageRef.current = null
      // Update credits from error response. `required` is the cost of a whole
      // turn, not of one message — writing it into costPerMessage doubled the
      // per-message cost everywhere it is displayed.
      setCredits({
        available: error.credits.available,
        costPerInteraction: error.credits.required,
        costPerMessage: credits?.costPerMessage ?? error.credits.required / 2,
        dailyLimit: credits?.dailyLimit ?? 100,
        resetsAt: error.credits.resetsAt,
        totalUsedToday:
          error.credits.totalUsedToday ?? credits?.totalUsedToday ?? 0,
      })
      // Open the credit limit modal
      setIsCreditLimitModalOpen(true)
    },
    onSuccess: (data) => {
      setIsTyping(false)
      setPendingMessage(null)
      pendingMessageRef.current = null
      // Set the AI message as streaming
      setStreamingMessageId(data.message.id)
      // Add both user message and AI response to local messages
      setLocalMessages((prev) => [
        ...prev,
        {
          id: data.userMessage.id,
          isUser: true,
          text: data.userMessage.content,
        },
        {
          id: data.message.id,
          isUser: false,
          text: data.message.content,
        },
      ])
      // Update credits from response
      if (data.credits) {
        setCredits(data.credits)
        // Update query cache for TopBar sync
        queryClient.setQueryData([QueryKey.GET_CREDITS], {
          data: data.credits,
          success: true,
        })
      }
    },
  })

  // Create conversation mutation
  const createConversationMutation = useCreateAiChatConversationMutation({
    onError: () => {
      setIsTyping(false)
      setPendingMessage(null)
      pendingMessageRef.current = null
    },
    onSuccess: (data) => {
      setConversationId(data.conversation.id)
      setHasConversation(true)
      // Send the pending message after conversation is created
      // Use ref to get the current value (avoids stale closure)
      if (pendingMessageRef.current) {
        sendMessageMutation.mutate({
          content: pendingMessageRef.current,
          conversationId: data.conversation.id,
        })
      }
    },
  })

  // Convert API messages to display format
  const apiMessages: DisplayMessage[] =
    conversationData?.messages?.map((msg) => ({
      id: msg.id,
      isUser: msg.role === 'user',
      text: msg.content,
    })) || []

  // Merge API messages with local messages, avoiding duplicates
  const messages: DisplayMessage[] = [
    ...apiMessages,
    ...localMessages.filter(
      (localMsg) => !apiMessages.some((apiMsg) => apiMsg.id === localMsg.id)
    ),
  ]

  const conversationTitle = conversationData?.conversation?.title || ''
  const hasConversation =
    !!conversationId || messages.length > 0 || !!pendingMessage

  // Check if pending message is already in messages (to avoid duplicates)
  const showPendingMessage =
    pendingMessage &&
    !messages.some((msg) => msg.isUser && msg.text === pendingMessage)

  const isSending =
    createConversationMutation.isPending || sendMessageMutation.isPending

  // Callback when streaming completes
  const handleStreamComplete = useCallback(() => {
    setStreamingMessageId(null)
  }, [])

  // Clear local messages and streaming state when conversation changes
  useEffect(() => {
    setLocalMessages([])
    setStreamingMessageId(null)
  }, [conversationId])

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isTyping])

  // Continuous scroll during streaming
  useEffect(() => {
    if (!streamingMessageId) return

    const intervalId = setInterval(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)

    return () => clearInterval(intervalId)
  }, [streamingMessageId])

  // Reset conversation state when leaving the page
  useEffect(() => {
    return () => {
      clearConversation()
    }
  }, [clearConversation])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!question.trim() || isSending) return

    const messageContent = question.trim()
    setQuestion('')
    setIsTyping(true)
    setPendingMessage(messageContent)
    pendingMessageRef.current = messageContent

    if (!conversationId) {
      // Create new conversation first, then send message
      createConversationMutation.mutate(undefined)
    } else {
      // Send message to existing conversation
      sendMessageMutation.mutate({
        content: messageContent,
        conversationId,
      })
    }
  }

  // Render loading state (only for loading existing conversations, not during first message)
  if (isLoadingConversation && conversationId && !pendingMessage) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>AI Assistant</h1>
          </div>
          <div className={styles.aiBadge}>
            <AIBadgeSparkle className={styles.aiBadgeIcon} />
            Generated by AI. Review Recommended.
          </div>
        </div>
        <div className={styles.loadingContainer}>
          <div className={styles.typingDots}>
            <span className={styles.typingDot} />
            <span className={styles.typingDot} />
            <span className={styles.typingDot} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>AI Assistant</h1>
        </div>

        {hasConversation && conversationTitle && (
          <span className={styles.conversationTitle}>{conversationTitle}</span>
        )}

        <div className={styles.aiBadge}>
          <AIBadgeSparkle className={styles.aiBadgeIcon} />
          Generated by AI. Review Recommended.
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {isCreditsExhausted && !hasConversation ? (
          /* Credit limit reached state - only show when NO conversation exists */
          <div className={styles.creditLimitWrapper}>
            <CreditLockIcon />
            <h2 className={styles.creditLimitTitle}>
              Daily AI Credit Limit Reached
            </h2>
            <p className={styles.creditLimitDescription}>
              You&apos;ve used all {credits?.dailyLimit ?? 100} AI credits
              allocated for today.
              <br />
              Your credits will automatically reset in{' '}
              <span className={styles.creditLimitCountdown}>
                {resetTimeDisplay || 'a few hours'}
              </span>
              . To continue using
              <br />
              the AI Assistant now, contact support.
            </p>
            <button
              className={styles.contactSupportButton}
              onClick={() => setIsSupportModalOpen(true)}
              type="button"
            >
              Contact Support
            </button>
            <form
              className={classNames(
                styles.inputContainer,
                styles.inputDisabled
              )}
            >
              <input
                className={styles.input}
                disabled
                placeholder="Ask a question..."
                type="text"
                value=""
              />
              <div className={styles.inputActions}>
                <button className={styles.micButton} disabled type="button">
                  <MicrophoneIcon className={styles.micIcon} />
                </button>
                <button className={styles.sendButton} disabled type="submit">
                  <SendArrowIcon className={styles.sendIcon} />
                </button>
              </div>
            </form>
          </div>
        ) : !hasConversation ? (
          /* Welcome section with input - centered together */
          <div className={styles.welcomeWrapper}>
            <img
              alt="AI Sparkle"
              className={styles.sparkleIcon}
              src="/image.png"
            />
            <h2 className={styles.heading}>
              How can I help you with internet intelligence?
            </h2>
            <form
              className={classNames(styles.inputContainer, {
                [styles.inputDisabled]: isSending,
              })}
              onSubmit={handleSubmit}
            >
              <input
                className={styles.input}
                disabled={isSending}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Ask a question..."
                type="text"
                value={question}
              />
              <div className={styles.inputActions}>
                <button className={styles.micButton} type="button">
                  <MicrophoneIcon className={styles.micIcon} />
                </button>
                <button
                  className={classNames(styles.sendButton, {
                    [styles.sendButtonActive]: question.trim() && !isSending,
                  })}
                  disabled={isSending}
                  type="submit"
                >
                  <SendArrowIcon className={styles.sendIcon} />
                </button>
              </div>
            </form>
          </div>
        ) : (
          /* Conversation mode */
          <>
            <div className={styles.messagesContainer}>
              {messages.map((message) => (
                <div
                  className={
                    message.isUser ? styles.userMessage : styles.aiMessage
                  }
                  key={message.id}
                >
                  {message.isUser ? (
                    <div className={styles.userContent}>
                      <div className={styles.userBubble}>{message.text}</div>
                      <div className={styles.userCost}>
                        <svg
                          className={styles.questionCostIcon}
                          fill="none"
                          height="16"
                          viewBox="0 0 16 16"
                          width="16"
                        >
                          <path
                            d="M10.334 7H5.66732"
                            stroke="#757575"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeMiterlimit="10"
                            strokeWidth="1.5"
                          />
                          <path
                            d="M11.3327 12.2867H8.66602L5.69934 14.26C5.25934 14.5533 4.66602 14.24 4.66602 13.7067V12.2867C2.66602 12.2867 1.33268 10.9533 1.33268 8.95333V4.95333C1.33268 2.95333 2.66602 1.62 4.66602 1.62H11.3327C13.3327 1.62 14.666 2.95333 14.666 4.95333V8.95333C14.666 10.9533 13.3327 12.2867 11.3327 12.2867Z"
                            stroke="#757575"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeMiterlimit="10"
                            strokeWidth="1.5"
                          />
                        </svg>
                        <span className={styles.questionCostLabel}>
                          Question Cost:
                        </span>
                        <span className={styles.questionCostValue}>
                          -{costPerInteraction} Credits
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.aiContent}>
                      <div className={styles.aiText}>
                        {streamingMessageId === message.id ? (
                          <TypewriterText
                            onComplete={handleStreamComplete}
                            speed={8}
                            text={message.text}
                          >
                            {(text) => (
                              <Markdown
                                components={{
                                  a: ({ ...props }) => (
                                    <a
                                      {...props}
                                      rel="noopener noreferrer"
                                      target="_blank"
                                    />
                                  ),
                                }}
                              >
                                {text}
                              </Markdown>
                            )}
                          </TypewriterText>
                        ) : (
                          <Markdown
                            components={{
                              a: ({ ...props }) => (
                                <a
                                  {...props}
                                  rel="noopener noreferrer"
                                  target="_blank"
                                />
                              ),
                            }}
                          >
                            {message.text}
                          </Markdown>
                        )}
                      </div>
                      <div className={styles.aiActions}>
                        <button
                          className={styles.actionButton}
                          onClick={() =>
                            navigator.clipboard.writeText(message.text)
                          }
                          type="button"
                        >
                          <CopyIcon className={styles.copyIcon} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Show pending message optimistically (if not already in messages) */}
              {showPendingMessage && (
                <div className={styles.userMessage}>
                  <div className={styles.userBubble}>{pendingMessage}</div>
                </div>
              )}

              {/* Typing indicator */}
              {isTyping && (
                <div className={styles.typingIndicator}>
                  <div className={styles.typingContent}>
                    <div className={styles.typingDots}>
                      <span className={styles.typingDot} />
                      <span className={styles.typingDot} />
                      <span className={styles.typingDot} />
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
            <form
              className={classNames(styles.inputContainer, {
                [styles.inputDisabled]: isSending || isCreditsExhausted,
              })}
              onSubmit={handleSubmit}
            >
              <input
                className={styles.input}
                disabled={isSending || isCreditsExhausted}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder={
                  isCreditsExhausted
                    ? 'No credits available...'
                    : 'Ask a question...'
                }
                type="text"
                value={question}
              />
              <div className={styles.inputActions}>
                <button
                  className={styles.micButton}
                  disabled={isCreditsExhausted}
                  type="button"
                >
                  <MicrophoneIcon className={styles.micIcon} />
                </button>
                <button
                  className={classNames(styles.sendButton, {
                    [styles.sendButtonActive]:
                      question.trim() && !isSending && !isCreditsExhausted,
                  })}
                  disabled={isSending || isCreditsExhausted}
                  type="submit"
                >
                  <SendArrowIcon className={styles.sendIcon} />
                </button>
              </div>
            </form>
          </>
        )}
      </div>

      <CreditLimitModal
        dailyLimit={credits?.dailyLimit}
        isOpen={isCreditLimitModalOpen}
        onClose={() => setIsCreditLimitModalOpen(false)}
        onContactSupport={() => {
          setIsCreditLimitModalOpen(false)
          setIsSupportModalOpen(true)
        }}
        resetsAt={credits?.resetsAt}
      />

      <SupportContactModal
        isOpen={isSupportModalOpen}
        onClose={() => setIsSupportModalOpen(false)}
      />
    </div>
  )
}
